import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  ConciergeInitModel,
  ConciergeInitVariables,
  ConciergeModel,
  ConciergeVariables,
} from '../../../../models/email-template/concierge.interface';

/**
 * Mockup-styled Gift Voucher noCTA block - a drop-in replacement for
 * app-concierge in block-renderer-v2 (same [block]/[isPreview] inputs and
 * outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation),
 * app-field-editor-v2 and app-pin sub-components and the exact same
 * ConciergeVariables field names - only the surrounding layout is new. Same
 * image-left/content-right pattern as Gift Voucher CTA (see
 * gift-voucher-v2), just without the CTA link/text fields or button.
 */
@Component({
  selector: 'app-concierge-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './concierge-v2.component.html',
  styleUrl: './concierge-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ConciergeComponentV2 extends BaseComponent implements OnInit {
  @Input() block: ConciergeModel = new ConciergeInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new ConciergeInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt';
  headingPlaceholder = 'Enter Heading Text';
  textPlaceholder = 'Enter Text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get imagePreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['ConciergeImageHref']);
    const segment = this.block.variables?.['ConciergeImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof ConciergeVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
